/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  BulkActionsToolbar,
  Button,
  Confirm,
  FilterButton,
  ListContextProvider,
  ListToolbar,
  TopToolbar,
  useList,
  useNotify,
} from "react-admin";
import { useFormState } from "react-final-form";

import { isSubscriptionDisabled, logError } from "../../../utils/appUtils";
import { deepRedirect } from "../../../utils/utils";

import { CreateIcon, ExportIcon } from "../../../components/common/iconography";

import { ImportServicesMenuButton } from "./ImportServicesMenuButton";
import { fetchServices } from "./ServiceUtils";
import {
  BulkDeleteServicesButton,
  exportServicesToCsv,
  getListFilters,
  PaginationToolbar,
  ServicesRatesDatagrid,
} from "./ServicesTabShared";

const useStyles = makeStyles((theme) => ({
  actionButton: {
    marginLeft: theme.spacing(1),
  },
}));

const exportList = (services, account) => {
  const fileNamePrefix = account.displayName || account.businessName;
  exportServicesToCsv(services, fileNamePrefix);
};

const ListActions = (props) => {
  const classes = useStyles();
  const {
    account,
    allServices,
    backPath,
    history,
    formState,
    openConfirm,
    xsDown,
    handleRefresh,
  } = props;
  // legacy import dialog state removed in favor of ImportServicesMenuButton

  const handleCreate = () => {
    if (formState.dirty) openConfirm("/services/create");
    else deepRedirect(history, "/services/create", backPath, { account });
  };

  return (
    <TopToolbar>
      <FilterButton />
      <Button
        label="Create"
        color="primary"
        className={classes.actionButton}
        onClick={handleCreate}
        disabled={isSubscriptionDisabled()}
      >
        <CreateIcon />
      </Button>
      {!xsDown && (
        <>
          <ImportServicesMenuButton
            account={account}
            existingServices={allServices}
            handleRefresh={handleRefresh}
            disabled={isSubscriptionDisabled()}
          />
          <Button
            label="Export"
            size="small"
            color="primary"
            onClick={() => exportList(allServices, account)}
            disabled={!allServices?.length}
          >
            <ExportIcon />
          </Button>
        </>
      )}
    </TopToolbar>
  );
};

// Direct fetch allows account to load faster.
export const fetchTabData = async (
  account,
  setAllServices,
  setServices,
  setLoading,
  notify,
) => {
  try {
    setLoading && setLoading(true);
    const allItems = await fetchServices(account.id, true);
    setAllServices(allItems);
    setServices && setServices(allItems);
  } catch (error) {
    logError("ServicesTab:fetchTabData", error, notify);
  } finally {
    setLoading && setLoading(false);
  }
};

export const ServicesTab = ({
  account,
  backPath,
  allServices,
  setAllServices,
  ...props
}) => {
  const notify = useNotify();
  const history = useHistory();
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPath, setConfirmPath] = useState("");
  const formState = useFormState({ subscription: { dirty: true } });
  const defaultSort = { field: "name", order: "ASC" };
  // const fixedFilter = { active: "true" };

  // memorize perPage settings
  const sessionPerPage = sessionStorage.getItem(`ServicesTab`);
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 10,
  );

  useEffect(() => {
    fetchTabData(account, setAllServices, setServices, setLoading, notify);
  }, []);

  // Refresh callback for upload dialog
  const handleRefresh = () => {
    fetchTabData(account, setAllServices, setServices, null, notify);
  };

  const listContext = useList({
    ...props,
    data: services || [],
    ids: services?.map(({ id }) => id) || [],
    loading: loading,
    loaded: !loading || allServices?.length > 0, // renders loading bars if false
    // filter: fixedFilter, // filtered in query
    sort: defaultSort,
    perPage: defaultPerPage,
    basePath: "/services",
    resource: "services",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      sessionStorage.setItem(`ServicesTab`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));

      // When perPage changes go back to page 1
      if (listContext.page === 1) return;
      listContext.setPage?.(1);
    }
  }, [listContext?.perPage]);

  const openConfirm = (redirectPath) => {
    setConfirmPath(redirectPath);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (!confirmPath) return;
    deepRedirect(history, confirmPath, backPath, { account });
  };

  return (
    <ListContextProvider value={listContext}>
      <ListToolbar
        {...props}
        filters={getListFilters(allServices, setServices)}
        actions={
          <ListActions
            account={account}
            history={history}
            allServices={allServices}
            formState={formState}
            openConfirm={openConfirm}
            xsDown={xsDown}
            handleRefresh={handleRefresh}
          />
        }
      />
      <BulkActionsToolbar>
        <BulkDeleteServicesButton
          allServices={allServices}
          handleRefresh={handleRefresh}
        />
      </BulkActionsToolbar>
      <ServicesRatesDatagrid
        allServices={allServices}
        xsDown={xsDown}
        enableSelection
        onOpenService={(id) => {
          if (formState.dirty) openConfirm(`/services/${id}`);
          else deepRedirect(history, `/services/${id}`, backPath, { account });
        }}
        onEdit={(record) => {
          if (formState.dirty) openConfirm(`/services/${record.id}`);
          else
            deepRedirect(history, `/services/${record.id}`, backPath, {
              account,
            });
        }}
      />
      <PaginationToolbar {...props} />
      <Confirm
        isOpen={confirmOpen}
        title="Save Confirmation"
        content="You have un-saved changes. If you continue, all changes will be lost. Proceed?"
        onConfirm={() => handleConfirm()}
        onClose={() => setConfirmOpen(false)}
      />
    </ListContextProvider>
  );
};
